<template>
  <div class="p-2 space-y-2">

    <!-- Search filter (only shown when there are many branches) -->
    <input
      v-if="branches.length > 7"
      v-model="filter"
      class="w-full bg-gray-700 text-xs text-gray-100 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
      placeholder="Filter branches…"
    />

    <!-- Branch list -->
    <div class="space-y-px max-h-44 overflow-y-auto">
      <div
        v-for="branch in filteredBranches"
        :key="branch"
        class="flex items-center gap-2 px-2 py-1 rounded text-xs transition-colors"
        :class="branch === current
          ? 'bg-blue-600/20 text-blue-300 cursor-default'
          : 'hover:bg-gray-700 text-gray-200 cursor-pointer'"
        @click="branch !== current && $emit('checkout', branch)"
      >
        <span class="text-[10px] w-2.5 shrink-0 text-center">{{ branch === current ? '●' : '○' }}</span>
        <span class="flex-1 font-mono truncate">{{ branch }}</span>
        <span v-if="branch === current" class="text-[9px] text-blue-400/70 shrink-0 uppercase tracking-wider">HEAD</span>
      </div>
      <div v-if="!filteredBranches.length" class="px-2 py-1.5 text-xs text-gray-600 text-center">
        No branches match
      </div>
    </div>

    <!-- Create branch -->
    <div class="border-t border-gray-700/60 pt-2">
      <div class="flex gap-1">
        <input
          ref="nameInput"
          v-model="newName"
          class="flex-1 bg-gray-700 text-xs text-gray-100 rounded px-2 py-1 focus:outline-none focus:ring-1 font-mono"
          :class="nameError ? 'ring-1 ring-red-500' : 'focus:ring-blue-500'"
          placeholder="new-branch-name"
          @keydown.enter.prevent="create"
          @input="nameError = ''"
        />
        <button
          class="text-xs bg-green-700 hover:bg-green-600 disabled:opacity-40 rounded px-2 shrink-0 transition-colors"
          :disabled="!newName.trim() || !!nameError"
          @click="create"
        >
          +
        </button>
      </div>
      <p v-if="nameError" class="text-[10px] text-red-400 mt-1">{{ nameError }}</p>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const props = defineProps<{ branches: string[]; current: string }>()
const emit  = defineEmits<{
  (e: 'checkout', branch: string): void
  (e: 'create',   name:   string): void
}>()

const newName   = ref('')
const filter    = ref('')
const nameError = ref('')

const filteredBranches = computed(() =>
  filter.value
    ? props.branches.filter(b => b.toLowerCase().includes(filter.value.toLowerCase()))
    : props.branches
)

function validate(name: string): string {
  if (!name.trim())               return 'Branch name is required'
  if (/\s/.test(name))            return 'Branch names cannot contain spaces'
  if (/[~^:?*[\\\s]/.test(name)) return 'Invalid characters in branch name'
  if (props.branches.includes(name)) return `Branch "${name}" already exists`
  return ''
}

function create(): void {
  const name = newName.value.trim()
  const err  = validate(name)
  if (err) { nameError.value = err; return }
  emit('create', name)
  newName.value   = ''
  nameError.value = ''
}
</script>
